/**
 * PERSONAL SECURITY — plan-014 unit C1 (design §27.3.9, §27.9.2, §27.10.2; FR12-SEC-01/02/03; ADR 0037, 0038).
 *
 * The person-global records of migration 0084 as types, the store seam between `apps/api/src/identity/{mfa,
 * sign-in-code,profile}/**` (the behaviour) and `packages/db/src/identity/person-security-store.ts` (the SQL), and
 * the few PURE rules both sides share (the last usable sign-in method, when MFA is enforced).
 *
 * WHY A SECOND STORE AND NOT MORE VERBS ON `IdentityStore`. `IdentityStore` (`./store.ts`) is P5's and A2's seam;
 * C1 owns neither. {@link PersonSecurityStoreRunner} hands the caller BOTH stores bound to ONE identity
 * db_transaction, so a sign-in code that issues an auth_session, or a TOTP confirmation that clears a lockout
 * counter, is still one unit of work.
 *
 * NOTHING SECRET IS TYPED HERE AS A VALUE THAT COULD BE STORED. A seed is a `Uint8Array` that only the
 * {@link import("../port/person-mfa-vault.js").PersonMfaVault} ever receives; every code is a keyed digest.
 */
import { idCodec } from "../id/brand.js";
export const personMfaFactorId = idCodec("PersonMfaFactorId");
export const personRecoveryCodeId = idCodec("PersonRecoveryCodeId");
export const signInCodeChallengeId = idCodec("SignInCodeChallengeId");
export const personEmailChangeId = idCodec("PersonEmailChangeId");
/** How many usable sign-in methods the facts hold. */
export function usableSignInMethodCount(facts) {
    return (facts.password ? 1 : 0) + (facts.google ? 1 : 0) + (facts.microsoft ? 1 : 0);
}
/**
 * Whether removing `kind` would leave the person with no way to sign in. Removing a method the person does not
 * hold is never "the last one" (it removes nothing).
 */
export function removingLeavesNoSignInMethod(facts, kind) {
    if (!facts[kind])
        return false;
    return usableSignInMethodCount(facts) <= 1;
}
/**
 * FR12-SEC-03: "MFA enforcement is available only after TOTP and recovery exist." A person's second step is asked for
 * at sign-in once they hold an ACTIVE authenticator. The recovery-code half of the sentence gates only the FIRST
 * enforcement, and it holds by construction: the confirmation that activates a person's first authenticator issues
 * their recovery codes in the same db_transaction (`MfaService.confirmEnrolment`), so an authenticator never becomes
 * active without a way back having existed.
 *
 * AMENDED 2026-10-06 (security review M-6): enforcement used to read "an active authenticator AND a usable recovery
 * code", so redeeming the LAST recovery code silently switched the second step off at sign-in. It no longer depends on
 * how many codes are left; a person with none left is told to make new ones (My security shows the count, and the
 * challenge answer carries it).
 */
export function mfaEnforced(activeTotp) {
    return activeTotp;
}
/**
 * The external-identity kinds behind a sign-in method name. AMENDED 2026-10-25 (design §39.6, ADR 0080): `microsoft`
 * is both the public Microsoft sign-in (`microsoft`) and an organization's SSO (`microsoft_entra`) — the person sees
 * one "Microsoft" method, whichever door they came through.
 */
export function providerKindsOf(kind) {
    return kind === "google" ? ["google"] : ["microsoft", "microsoft_entra"];
}
//# sourceMappingURL=person-security.js.map